import { useState, useEffect, CSSProperties, ReactNode } from 'react';
import {
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  DragIndicatorRounded as DragIndicatorIcon,
  KeyboardArrowDownRounded as KeyboardArrowDownIcon,
  KeyboardArrowUpRounded as KeyboardArrowUpIcon,
} from '@material-ui/icons';

import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { TableBodySkeleton } from '../../../shared/components/Tables/TableBodySkeleton.component';
import { ArchivableRowActions } from '../../../shared/components/Tables/ArchivableRowActions.component';
import { ROWS_PER_PAGE_OPTIONS } from '../../../shared/constants/table.constants';
import { Category } from '../../types/category.type';
import StatusChip from '../../../shared/components/Chips/StatusChip.component';
import { SubCategory } from '../../types/sub-category.type';
import { useAdminGetSubCategories } from '../../hooks/admin-get-sub-categories.hook';
import { useUpdateSubCategory } from '../../hooks/update-sub-category.hook';
import { useUpdateSubCategoriesPositions } from '../../hooks/update-sub-categories-positions.hook';

interface CategoriesTableHeader {
  key: string;
  label: string;
  ariaLabel?: string;
  minWidth: number;
  align: 'left' | 'right' | 'center';
}

interface CategoriesTableProps {
  categories: Category[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  isLoading: boolean;
  isDragEnabled: boolean;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onPositionsChange: (categories: { id: string; position: number }[]) => void;
  onToggleActive: (categoryId: string, isActive: boolean) => Promise<boolean>;
  onEdit: (category: Category) => void;
  onArchive: (category: Category) => void;
  onRestore: (category: Category) => void;
}

type CategoryRowActions = Pick<CategoriesTableProps, 'onEdit' | 'onArchive' | 'onRestore'>;

const TABLE_HEADERS: CategoriesTableHeader[] = [
  { key: 'order', label: '', minWidth: 120, align: 'left' },
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left' },
  { key: 'description', label: 'Description', minWidth: 250, align: 'left' },
  { key: 'active', label: 'Statut', minWidth: 80, align: 'center' },
  { key: 'actions', label: '', ariaLabel: 'Actions', minWidth: 90, align: 'right' },
];

const COLUMN_COUNT = TABLE_HEADERS.length;

const SUB_CATEGORY_HEADERS: CategoriesTableHeader[] = [
  { key: 'order', label: '', minWidth: 50, align: 'left' },
  { key: 'name', label: 'Nom', minWidth: 200, align: 'left' },
  { key: 'active', label: 'Statut', minWidth: 80, align: 'center' },
];

const useStyles = makeStyles((theme) => ({
  positionChip: {
    backgroundColor: theme.palette.admin.primary.main,
    color: theme.palette.admin.primary.contrastText,
    fontWeight: 600,
    fontSize: 12,
    borderRadius: 4,
  },
  order: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'left',
    gap: '1rem',
  },
  drag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
  },
  dragHandle: {
    cursor: 'grab',
    opacity: 0.6,
  },
  statusChip: {
    cursor: 'pointer',

    '& > .MuiChip-root': {
      cursor: 'pointer',
    },
  },
}));

const useRowStyles = makeStyles({
  categoryRow: (props: { isOpen: boolean }) => ({
    '& > .MuiTableCell-root': {
      borderBottom: props.isOpen ? 0 : '1px solid rgba(224, 224, 224, 1)',
    },
  }),
  collapseRow: (props: { isOpen: boolean }) => ({
    '& > .MuiTableCell-root': props.isOpen
      ? { padding: '0.5rem', borderBottom: '1px solid rgba(224, 224, 224, 1)' }
      : { padding: '0 0.5rem', borderBottom: 0 },
  }),
  collapse: {
    '& .MuiPaper-root': {
      padding: '1rem',
    },
  },
});

function StatusCell({
  isActive,
  isArchived,
  onToggle,
}: {
  isActive: boolean;
  isArchived: boolean;
  onToggle: () => void;
}) {
  const classes = useStyles();

  if (isArchived) {
    return (
      <TableCell align="center">
        <StatusChip isActive={isActive} archived />
      </TableCell>
    );
  }

  return (
    <TableCell align="center">
      <span className={classes.statusChip} onClick={onToggle}>
        <StatusChip isActive={isActive} />
      </span>
    </TableCell>
  );
}

function SortableSubCategoryRow({
  subCategory,
  onToggleActive,
}: {
  subCategory: SubCategory;
  onToggleActive: (subCategoryId: string, isActive: boolean) => void;
}) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: subCategory.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover tabIndex={-1}>
      <TableCell align="left" style={{ width: 65 }}>
        <div className={classes.drag}>
          <DragIndicatorIcon {...attributes} {...listeners} className={classes.dragHandle} />
          <Chip className={classes.positionChip} size="small" label={subCategory.position} />
        </div>
      </TableCell>
      <TableCell align="left">{subCategory.name}</TableCell>
      <StatusCell
        isActive={subCategory.isActive}
        isArchived={Boolean(subCategory.archivedAt)}
        onToggle={() => onToggleActive(subCategory.id, !subCategory.isActive)}
      />
    </TableRow>
  );
}

function CategorySubCategoriesTable({ categoryId }: { categoryId: string }) {
  const { isLoading, isRefetching, subCategories } = useAdminGetSubCategories({
    filters: { category: categoryId },
    sortBy: 'position',
    sortDirection: 'asc',
  });
  const isFetching = isLoading || isRefetching;
  const { updateSubCategory } = useUpdateSubCategory();
  const { updatePositions } = useUpdateSubCategoriesPositions();

  const [localSubCategories, setLocalSubCategories] = useState<SubCategory[]>(subCategories);

  useEffect(() => {
    setLocalSubCategories(subCategories);
  }, [subCategories]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleToggleActive = async (subCategoryId: string, isActive: boolean) => {
    const updated = await updateSubCategory({ subCategoryId, subCategoryData: { isActive } });
    if (updated) {
      setLocalSubCategories((previous) =>
        previous.map((subCategory) => (subCategory.id === subCategoryId ? { ...subCategory, isActive } : subCategory))
      );
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localSubCategories.findIndex((subCategory) => subCategory.id === active.id);
    const newIndex = localSubCategories.findIndex((subCategory) => subCategory.id === over.id);

    const reordered = arrayMove(localSubCategories, oldIndex, newIndex);
    const withPositions = reordered.map((subCategory, index) => ({ ...subCategory, position: index + 1 }));

    setLocalSubCategories(withPositions);
    updatePositions(withPositions.map((subCategory) => ({ id: subCategory.id, position: subCategory.position })));
  };

  const columnCount = SUB_CATEGORY_HEADERS.length;

  return (
    <Table size="small" stickyHeader style={{ border: 'solid 2px #f6f6f6', borderRadius: '4px 4px 0 0' }}>
      <TableHead>
        <TableRow>
          {SUB_CATEGORY_HEADERS.map((header) => (
            <TableCell
              align={header.align}
              style={{ fontWeight: 'bold', minWidth: header.minWidth }}
              key={header.key}
              aria-label={header.ariaLabel}
            >
              {header.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      {isFetching && <TableBodySkeleton rows={3} columns={columnCount} />}
      {!isFetching && localSubCategories.length === 0 && (
        <TableBody>
          <TableRow>
            <TableCell colSpan={columnCount} style={{ textAlign: 'center' }}>
              Aucune sous-rubrique
            </TableCell>
          </TableRow>
        </TableBody>
      )}
      {!isFetching && localSubCategories.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localSubCategories.map((subCategory) => subCategory.id)}
            strategy={verticalListSortingStrategy}
          >
            <TableBody>
              {localSubCategories.map((subCategory) => (
                <SortableSubCategoryRow
                  key={subCategory.id}
                  subCategory={subCategory}
                  onToggleActive={handleToggleActive}
                />
              ))}
            </TableBody>
          </SortableContext>
        </DndContext>
      )}
    </Table>
  );
}

type CategoryRowProps = CategoryRowActions & {
  category: Category;
  isOpen: boolean;
  onToggleExpand: () => void;
  onToggleActive: (categoryId: string, isActive: boolean) => void;
};

function CategoryRow({
  category,
  isOpen,
  onToggleExpand,
  onToggleActive,
  onEdit,
  onArchive,
  onRestore,
  dragHandle,
  rowRef,
  rowStyle,
}: CategoryRowProps & {
  dragHandle?: ReactNode;
  rowRef?: (element: HTMLElement | null) => void;
  rowStyle?: CSSProperties;
}) {
  const classes = useStyles();
  const rowClasses = useRowStyles({ isOpen });
  const isArchived = Boolean(category.archivedAt);

  return (
    <>
      <TableRow ref={rowRef} style={rowStyle} className={rowClasses.categoryRow} hover={!isOpen} tabIndex={-1}>
        <TableCell align="left">
          <div className={classes.order}>
            {dragHandle}
            <IconButton size="small" aria-label="Sous-rubriques" onClick={onToggleExpand}>
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </div>
        </TableCell>
        <TableCell align="left">{category.name}</TableCell>
        <TableCell align="left">{category.description}</TableCell>
        <StatusCell
          isActive={category.isActive}
          isArchived={isArchived}
          onToggle={() => onToggleActive(category.id, !category.isActive)}
        />
        <TableCell align="right">
          <ArchivableRowActions
            rowId={category.id}
            isArchived={isArchived}
            onEdit={() => onEdit(category)}
            onArchive={() => onArchive(category)}
            onRestore={() => onRestore(category)}
          />
        </TableCell>
      </TableRow>
      <TableRow className={rowClasses.collapseRow} tabIndex={-1}>
        <TableCell colSpan={COLUMN_COUNT}>
          <Collapse className={rowClasses.collapse} in={isOpen} timeout="auto" unmountOnExit>
            <CategorySubCategoriesTable categoryId={category.id} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SortableCategoryRow(props: CategoryRowProps) {
  const classes = useStyles();
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: props.category.id,
  });

  const dragHandle = (
    <div className={classes.drag}>
      <DragIndicatorIcon {...attributes} {...listeners} className={classes.dragHandle} />
      <Chip className={classes.positionChip} size="small" label={props.category.position} />
    </div>
  );

  return (
    <CategoryRow
      {...props}
      dragHandle={dragHandle}
      rowRef={setNodeRef}
      rowStyle={{ transform: CSS.Transform.toString(transform), transition }}
    />
  );
}

function CategoriesTable({
  categories,
  totalCount,
  page,
  rowsPerPage,
  isLoading,
  isDragEnabled,
  onPageChange,
  onRowsPerPageChange,
  onPositionsChange,
  onToggleActive,
  onEdit,
  onArchive,
  onRestore,
}: CategoriesTableProps) {
  const [localCategories, setLocalCategories] = useState(categories);
  const [expandedCategoryId, setExpandedCategoryId] = useState<string | null>(null);

  useEffect(() => {
    setLocalCategories(categories);
  }, [categories]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleToggleActive = async (categoryId: string, isActive: boolean): Promise<boolean> => {
    const success = await onToggleActive(categoryId, isActive);
    if (success) {
      setLocalCategories((previous) =>
        previous.map((category) => (category.id === categoryId ? { ...category, isActive } : category))
      );
    }
    return success;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = localCategories.findIndex((category) => category.id === active.id);
    const newIndex = localCategories.findIndex((category) => category.id === over.id);

    const reordered = arrayMove(localCategories, oldIndex, newIndex);
    const withPositions = reordered.map((category, index) => ({
      ...category,
      position: page * rowsPerPage + index + 1,
    }));

    setLocalCategories(withPositions);
    onPositionsChange(withPositions.map((category) => ({ id: category.id, position: category.position })));
  };

  const rowProps = (category: Category): CategoryRowProps => ({
    category,
    isOpen: expandedCategoryId === category.id,
    onToggleExpand: () => setExpandedCategoryId((previous) => (previous === category.id ? null : category.id)),
    onToggleActive: handleToggleActive,
    onEdit,
    onArchive,
    onRestore,
  });

  return (
    <Paper>
      <TableContainer style={{ maxHeight: '75svh' }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {TABLE_HEADERS.map((header) => (
                <TableCell
                  key={header.key}
                  aria-label={header.ariaLabel}
                  align={header.align}
                  style={{ minWidth: header.minWidth, fontWeight: 'bold' }}
                >
                  {header.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading && <TableBodySkeleton rows={rowsPerPage} columns={COLUMN_COUNT} />}
          {!isLoading && (
            <TableBody>
              {localCategories.length === 0 && (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} style={{ textAlign: 'center' }}>
                    Aucune rubrique
                  </TableCell>
                </TableRow>
              )}
              {isDragEnabled ? (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext
                    items={localCategories.map((category) => category.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localCategories.map((category) => (
                      <SortableCategoryRow key={category.id} {...rowProps(category)} />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                localCategories.map((category) => <CategoryRow key={category.id} {...rowProps(category)} />)
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
      />
    </Paper>
  );
}

export default CategoriesTable;
